<template>
    <el-dialog
        :visible="visible"
        :title="dialogTitle"
        width="600px"
        custom-class="tlc-dialog"
        :close-on-click-modal="false"
        append-to-body
        @update:visible="close"
        @closed="onClosed"
    >
        <el-form label-position="top" size="small" class="tlc-form" @submit.native.prevent>
            <el-form-item v-if="showTeamLead" label="Teamlead áp dụng" :error="errors.user_id">
                <el-select
                    v-model="form.user_id"
                    filterable
                    clearable
                    placeholder="Chọn Teamlead"
                    class="w-100"
                    :disabled="editing"
                >
                    <el-option v-for="tl in teamLeads" :key="tl.id" :label="tl.name" :value="tl.id">
                        <span>{{ tl.name }}</span>
                        <span class="tlc-opt-sub">{{ tl.team_name || tl.branch_name }}</span>
                    </el-option>
                </el-select>
                <div v-if="editing" class="tlc-form__hint">
                    Không đổi được Teamlead của lịch đã tạo — hãy xoá và tạo lịch mới nếu cần chuyển người.
                </div>
            </el-form-item>

            <el-form-item label="Nội dung công việc" :error="errors.task">
                <el-input
                    v-model="form.task"
                    maxlength="255"
                    show-word-limit
                    placeholder="VD: Họp review tuyển giáo viên, Thăm lớp A1..."
                />
            </el-form-item>

            <div class="tlc-form__row">
                <el-form-item label="Ngày" :error="errors.date" class="tlc-form__col">
                    <el-date-picker
                        v-model="form.date"
                        type="date"
                        value-format="yyyy-MM-dd"
                        format="dd/MM/yyyy"
                        placeholder="Chọn ngày"
                        :picker-options="{ firstDayOfWeek: 1 }"
                        class="w-100"
                    />
                </el-form-item>

                <el-form-item label="Giờ bắt đầu" class="tlc-form__col tlc-form__col--sm" :error="errors.time">
                    <el-time-select
                        v-model="form.start_time"
                        :picker-options="timePickerOptions"
                        placeholder="Bắt đầu"
                        class="w-100"
                        @change="syncEndTime"
                    />
                </el-form-item>
                <el-form-item label="Giờ kết thúc" class="tlc-form__col tlc-form__col--sm">
                    <el-time-select
                        v-model="form.end_time"
                        :picker-options="{ ...timePickerOptions, minTime: form.start_time }"
                        placeholder="Kết thúc"
                        class="w-100"
                    />
                </el-form-item>
            </div>

            <div class="tlc-form__row">
                <el-form-item label="Màu sắc nhãn" class="tlc-form__col">
                    <el-select v-model="form.color" class="w-100" placeholder="Tự động theo tên" clearable>
                        <el-option
                            v-for="c in colorOptions"
                            :key="c.name"
                            :value="c.name"
                            :label="c.label"
                        >
                            <span class="d-inline-flex align-items-center" style="gap: 8px;">
                                <span :style="{ display: 'inline-block', width: '10px', height: '10px', borderRadius: '50%', backgroundColor: c.dot }"></span>
                                <span>{{ c.label }}</span>
                            </span>
                        </el-option>
                    </el-select>
                </el-form-item>
            </div>

            <div v-if="previewText" class="tlc-form__preview">
                <i class="el-icon-magic-stick"></i> {{ previewText }}
            </div>
        </el-form>

        <span slot="footer">
            <el-button size="small" @click="close">Huỷ</el-button>
            <el-button size="small" type="primary" :loading="submitting" @click="submit">
                {{ editing ? "Lưu thay đổi" : "Tạo lịch" }}
            </el-button>
        </span>
    </el-dialog>
</template>

<script>
import moment from "moment-timezone";
import {
    DEFAULT_EVENT_MINUTES,
    hmToMinutes,
    minutesToHm,
    TASK_COLOR_PALETTES,
} from "@/core/config/teamLeadsCalendar/teamLeadsCalendarOption";

const DEFAULT_START_TIME = "08:00";
const DEFAULT_END_TIME = minutesToHm(hmToMinutes(DEFAULT_START_TIME) + DEFAULT_EVENT_MINUTES);

/** "08:00:00" | "08:00" -> "08:00" */
function toHm(value) {
    const m = value ? String(value).match(/^(\d{1,2}):(\d{2})/) : null;
    return m ? `${m[1].padStart(2, "0")}:${m[2]}` : "";
}

/** "08:00" -> "08:00:00" (định dạng backend yêu cầu) */
function toHms(value) {
    const hm = toHm(value);
    return hm ? `${hm}:00` : null;
}

function blankForm() {
    return {
        id: null,
        user_id: null,
        task: "",
        date: moment().format("YYYY-MM-DD"),
        start_time: DEFAULT_START_TIME,
        end_time: DEFAULT_END_TIME,
        color: "",
    };
}

export default {
    name: "ScheduleFormDialog",
    props: {
        visible: { type: Boolean, default: false },
        teamLeads: { type: Array, default: () => [] },
        /** Chỉ hiện ô chọn Teamlead khi tài khoản được quản lý lịch của người khác */
        showTeamLead: { type: Boolean, default: true },
        initial: { type: Object, default: () => ({}) },
        editing: { type: Boolean, default: false },
        submitting: { type: Boolean, default: false },
        currentUserId: { type: [Number, String], default: null },
    },
    data() {
        return {
            form: blankForm(),
            errors: {},
            timePickerOptions: { start: "06:00", step: "00:15", end: "23:00" },
            colorOptions: TASK_COLOR_PALETTES,
        };
    },
    computed: {
        dialogTitle() {
            return this.editing ? "Chỉnh sửa lịch" : "Tạo lịch mới";
        },
        previewText() {
            const f = this.form;
            if (!f.start_time || !f.date) return "";
            const day = this.capitalize(moment(f.date, "YYYY-MM-DD").format("dddd DD/MM/YYYY"));
            const time = `${f.start_time}${f.end_time ? `–${f.end_time}` : ""}`;
            return `${day} · ${time}`;
        },
    },
    watch: {
        visible(v) {
            if (v) this.hydrate();
        },
    },
    methods: {
        hydrate() {
            const base = blankForm();
            const init = this.initial || {};
            const raw = init.raw || {};

            base.id = init.id != null ? init.id : raw.id || null;
            base.user_id = raw.user_id != null ? raw.user_id : init.team_lead_id;
            if (base.user_id == null && !this.showTeamLead) base.user_id = this.currentUserId;

            base.task = raw.task || init.title || "";
            base.date = init.date || raw.date || base.date;
            base.start_time = toHm(init.start_time || raw.start_time) || base.start_time;
            base.end_time = toHm(init.end_time || raw.end_time) || base.end_time;
            base.color = raw.color || init.color || "";

            this.form = base;
            this.errors = {};
        },
        capitalize(v) {
            return v ? v.charAt(0).toUpperCase() + v.slice(1) : v;
        },
        /** Đổi giờ bắt đầu thì đẩy giờ kết thúc theo cho hợp lệ */
        syncEndTime(value) {
            const start = hmToMinutes(value);
            if (start == null) return;
            const end = hmToMinutes(this.form.end_time);
            if (end == null || end <= start) {
                this.form.end_time = minutesToHm(start + DEFAULT_EVENT_MINUTES);
            }
        },
        validate() {
            const e = {};
            const f = this.form;
            if (this.showTeamLead && !f.user_id) e.user_id = "Chọn Teamlead áp dụng lịch";
            if (!String(f.task || "").trim()) e.task = "Nhập nội dung công việc";
            if (!f.date) e.date = "Chọn ngày";
            if (!f.start_time) e.time = "Chọn giờ bắt đầu";
            else if (!f.end_time) e.time = "Chọn giờ kết thúc";
            else if (f.end_time <= f.start_time) e.time = "Giờ kết thúc phải sau giờ bắt đầu";
            this.errors = e;
            return !Object.keys(e).length;
        },
        submit() {
            if (!this.validate()) return;
            const f = this.form;
            this.$emit("submit", {
                editing: this.editing,
                id: f.id,
                payload: {
                    user_id: f.user_id || this.currentUserId,
                    task: String(f.task).trim(),
                    start_time: toHms(f.start_time),
                    end_time: toHms(f.end_time),
                    date: f.date,
                    color: f.color || undefined,
                },
            });
        },
        close() {
            this.$emit("update:visible", false);
        },
        onClosed() {
            this.form = blankForm();
            this.errors = {};
        },
    },
};
</script>

<style scoped>
.tlc-form__row {
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
}
.tlc-form__col {
    flex: 1;
    min-width: 150px;
    margin-bottom: 0;
}
.tlc-form__col--sm {
    min-width: 110px;
}
.tlc-form__hint {
    font-size: 11px;
    color: #99a0b0;
    margin-top: 4px;
}
.tlc-form__preview {
    margin-top: 14px;
    background: #f5f7ff;
    border: 1px dashed #d9def5;
    border-radius: 10px;
    padding: 8px 12px;
    font-size: 12px;
    color: #4f46e5;
}
.w-100 {
    width: 100%;
}
.tlc-opt-sub {
    float: right;
    color: #b1b7c4;
    font-size: 12px;
}
</style>
